/**
 * UseEcho — a project's ghost in another zone
 *
 * Place at: src/components/map/UseEcho.jsx
 * Drawn by: CosmicMap.jsx, beneath the real project nodes.
 *
 * A Use says "this exact work is ALSO used here": a stencil that lives in
 * Build is also a GOBO in Beautiful. The map shows that as a small, faint
 * copy of the node sitting at the edge of the OTHER zone, tied back to the
 * real node by a hairline in the home zone's color. One project, visibly
 * living in two places.
 *
 * The echo is not a second project. Clicking it selects the REAL node,
 * which lights the echo, the hairline and everything else connected.
 *
 * States: 'focus' (its project is selected) | 'ambient' (toggle on) |
 *         'dimmed' (something else selected).
 * Hover shows the Use note(s) through a native tooltip.
 */

import React from 'react';

const ECHO_R = 13;

const UseEcho = ({ echo, state, onClick }) => {
  const { x, y, homeNode, zone, notes, project } = echo;
  if (!homeNode) return null;

  const homeColor = homeNode.zone?.color || '#71717a';
  const echoColor = zone?.color || '#71717a';
  const focus = state === 'focus';
  const dimmed = state === 'dimmed';

  const lineOpacity = focus ? 0.55 : dimmed ? 0.03 : 0.14;
  const nodeOpacity = focus ? 1 : dimmed ? 0.12 : 0.55;
  const img = project?.image_url || project?.imageUrl;
  const clipId = `echo-clip-${echo.key}`;
  const tooltip = `${project?.name || 'Project'} — also used in ${zone?.label || zone?.shortLabel || 'another zone'}`
    + (notes.length ? `\n${notes.map((n) => `• ${n}`).join('\n')}` : '');

  return (
    <g>
      {/* Hairline back to the real node */}
      <line
        x1={homeNode.x} y1={homeNode.y} x2={x} y2={y}
        stroke={homeColor}
        strokeWidth={focus ? 1.2 : 0.7}
        strokeDasharray="1 4"
        strokeLinecap="round"
        opacity={lineOpacity}
        style={{ pointerEvents: 'none', transition: 'opacity 0.4s ease' }}
      >
        {focus && <animate attributeName="stroke-dashoffset" from="10" to="0" dur="1.4s" repeatCount="indefinite" />}
      </line>

      <g
        transform={`translate(${x}, ${y})`}
        opacity={nodeOpacity}
        style={{ cursor: 'pointer', transition: 'opacity 0.4s ease' }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onClick?.(project, e); }}
      >
        <title>{tooltip}</title>

        {/* Fat invisible hit area */}
        <circle r={ECHO_R + 6} fill="transparent" />

        {focus && (
          <circle r={ECHO_R + 6} fill="none" stroke={echoColor} strokeWidth={1} opacity={0.5}>
            <animate attributeName="r" values={`${ECHO_R + 3};${ECHO_R + 9};${ECHO_R + 3}`} dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0.1;0.5" dur="2.4s" repeatCount="indefinite" />
          </circle>
        )}

        {img ? (
          <>
            <defs>
              <clipPath id={clipId}><circle r={ECHO_R} /></clipPath>
            </defs>
            <image href={img} x={-ECHO_R} y={-ECHO_R} width={ECHO_R * 2} height={ECHO_R * 2}
              clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" opacity={0.7} />
          </>
        ) : (
          <>
            <circle r={ECHO_R} fill={homeColor} opacity={0.12} />
            <text textAnchor="middle" dominantBaseline="central" fontSize="10" fontWeight="700" fill={homeColor}>
              {project?.name?.[0]?.toUpperCase() || '?'}
            </text>
          </>
        )}

        {/* Dashed ring in the ECHO zone's color: "a copy that lives here" */}
        <circle r={ECHO_R + 1.5} fill="none" stroke={echoColor} strokeWidth={1.4} strokeDasharray="3 2.5" />
      </g>
    </g>
  );
};

export default React.memo(UseEcho);